import { token } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Divider, Flex } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import {
  Bleed,
  Main,
  ObjectHeader,
  Padding,
  SidePanel,
} from '../../common/components';
import {
  confirmPanelFormClose,
  Form,
  FormField,
  FormSubmitButton,
} from '../../common/form';
import { ValidationMessagesMap } from '../../common/utils/ValidationMessagesMap';
import {
  AlternativeAlgorithmSetDataType,
  AlternativeAlgorithmSetFormDataType,
} from '../schema';
import { AlternativeAlgorithmSetFormSchema } from '../schema/AlternativeAlgorithmFormSchema';
import translations from '../translations/AlternativeAlgorithmEditPanel.translations.json';
import { AlternativeAlgorithmExceptionFields } from './AlternativeAlgorithmExceptionFields';
import { AlternativeAlgorithmFields } from './AlternativeAlgorithmFields';
import {
  AlgorithmOptions,
  AlgorithmSetOption,
  ViewableGroupOrAlgorithm,
} from './types';
import {
  alternativeSetToEditFormData,
  editFormToAlternativeSet,
  getAlgorithmOptions,
  getIndexArray,
  recalculateOptions,
  validateAlternativeSet,
} from './utils';
import { AlgorithmExtendedType } from '../algorithms/schema/types';
import { AlternativeAlgorithmViewModal } from './AlternativeAlgorithmViewModal';

const { addTranslations, useTranslation } = i18nNsFactory(
  'alternative-edit-form',
);
addTranslations(translations);

interface AlternativeAlgorithmEditPanelProps {
  algorithms: AlgorithmExtendedType[];
  editableAlternative: AlternativeAlgorithmSetDataType;
  index?: number;
  onClose: () => void;
  onSubmit: (key: AlternativeAlgorithmSetDataType, index?: number) => void;
}

export function AlternativeAlgorithmEditPanel({
  algorithms,
  editableAlternative,
  index,
  onClose,
  onSubmit,
}: AlternativeAlgorithmEditPanelProps) {
  const { getValues } = useFormContext();
  const algorithmIdMap = useMemo(
    () => getAlgorithmOptions(algorithms),
    [algorithms],
  );
  const [preSaveErrors, setPreSaveErrors] = useState<ValidationMessagesMap>(
    new ValidationMessagesMap(),
  );

  const algorithmSet = useMemo(() => {
    return alternativeSetToEditFormData(editableAlternative);
  }, [editableAlternative]);
  const handleSubmit = useCallback(
    (formData: AlternativeAlgorithmSetFormDataType) => {
      const alternativeSet = editFormToAlternativeSet(formData, algorithmIdMap);
      const otherAlternativeSets: AlternativeAlgorithmSetDataType[] = [
        ...getValues('alternativeAlgorithmSets'),
      ];
      if (index !== undefined) {
        otherAlternativeSets.splice(index, 1);
      }
      const messages = validateAlternativeSet(
        alternativeSet.alternativeAlgorithms,
        formData.exceptions,
        otherAlternativeSets,
        algorithms,
        getValues('validPeriod'),
      );
      setPreSaveErrors(messages);
      if (messages.size === 0) {
        onSubmit(alternativeSet, index);
      }
    },
    [algorithmIdMap, algorithms, getValues, index, onSubmit],
  );

  return (
    <Form
      schema={AlternativeAlgorithmSetFormSchema}
      defaultValues={algorithmSet}
      onSubmit={handleSubmit}
      serverErrors={preSaveErrors}
      idAttributePrefix={`alternativeAlgorithm-${
        typeof index === 'undefined' ? 'create' : index
      }`}
    >
      <AlternativeAlgorithmSetEditForm
        algorithms={algorithms}
        algorithmIdMap={algorithmIdMap}
        onClose={onClose}
      />
    </Form>
  );
}

interface AlternativeAlgorithmSetEditFormProps {
  algorithms: AlgorithmExtendedType[];
  algorithmIdMap: Map<string, AlgorithmSetOption>;
  onClose: () => void;
}

function AlternativeAlgorithmSetEditForm({
  algorithms,
  algorithmIdMap,
  onClose,
}: AlternativeAlgorithmSetEditFormProps) {
  const { t } = useTranslation();
  const { isDirty } = useFormState();
  const [code, name] = useWatch({
    name: ['code', 'name'],
  });
  const handleCancel = useCallback(() => {
    if (isDirty) {
      confirmPanelFormClose(onClose);
    } else {
      onClose();
    }
  }, [onClose, isDirty]);

  return (
    <SidePanel
      minWidth={700}
      side="right"
      collapsedWidth={700}
      width={1786}
      mode="modal"
    >
      <SidePanelWrapper>
        <Padding>
          <Flex align="end" justify="space-between">
            <ObjectHeader
              upperText={t('alternative.code.label', {
                code,
              })}
              lowerText={name || t('alternative.heading.new')}
            />
          </Flex>
        </Padding>
        <Divider style={{ margin: 0 }} />
        <FormWrapper vertical>
          <Scrollable>
            <Padding>
              <AlternativeAlgorithmSetInner
                algorithms={algorithms}
                algorithmIdMap={algorithmIdMap}
              />
            </Padding>
          </Scrollable>
        </FormWrapper>
        <Main.Footer>
          <FullWidthFlex justify="end" gap="small">
            <Button id="cancel-key-edit" onClick={handleCancel}>
              {t('button.cancel')}
            </Button>
            <FormSubmitButton>{t('button.ok')}</FormSubmitButton>
          </FullWidthFlex>
        </Main.Footer>
      </SidePanelWrapper>
    </SidePanel>
  );
}

function AlternativeAlgorithmSetInner({
  algorithms,
  algorithmIdMap,
}: {
  algorithms: AlgorithmExtendedType[];
  algorithmIdMap: Map<string, AlgorithmSetOption>;
}) {
  const { t } = useTranslation();
  const [algorithmsCount, exceptionsCount] = useWatch({
    name: ['alternativeAlgorithms.length', 'exceptions.length'],
  });
  const { getValues } = useFormContext();
  const [options, setOptions] = useState<AlgorithmOptions>(() =>
    recalculateOptions(getValues('alternativeAlgorithms'), algorithmIdMap),
  );
  const recalculate = useCallback(
    () =>
      setOptions(
        recalculateOptions(getValues('alternativeAlgorithms'), algorithmIdMap),
      ),
    [algorithmIdMap, getValues],
  );

  useEffect(() => {
    setOptions(
      recalculateOptions(getValues('alternativeAlgorithms'), algorithmIdMap),
    );
  }, [algorithmIdMap, getValues]);

  const alternatives = useMemo(
    () => getIndexArray(Math.max(1, algorithmsCount)),
    [algorithmsCount],
  );
  const exceptions = useMemo(
    () => getIndexArray(Math.max(1, exceptionsCount)),
    [exceptionsCount],
  );

  const [viewableGroupOrAlgorithm, setViewableGroupOrAlgorithm] = useState<
    ViewableGroupOrAlgorithm | undefined
  >();

  const onViewAlternative = useCallback(
    (alternativeIndex: number, type: 'alternative' | 'exception') => {
      const algorithmSet = getValues(
        type === 'alternative' ? 'alternativeAlgorithms' : 'exceptions',
      );

      const algorithmIdParts = (
        algorithmSet[alternativeIndex]?.alternativeId ?? ''
      ).split('-');
      const [algorithmType, algorithmId] = algorithmIdParts;

      if (algorithmId) {
        setViewableGroupOrAlgorithm({
          code: algorithmType === 'code' ? algorithmId : undefined,
          groupCode: algorithmType === 'group' ? algorithmId : undefined,
          alternativeCode: getValues('code'),
        });
      }
    },
    [getValues],
  );

  return (
    <Flex vertical>
      <FormField name="name" label={t('alternative.name.label')} required />
      <Bleed direction="horizontal">
        <Divider orientation="left">{t('alternative.group.label')}</Divider>
      </Bleed>
      <Flex vertical gap="middle">
        {alternatives.map((index) => (
          <AlternativeAlgorithmFields
            key={index}
            algorithmIdMap={algorithmIdMap}
            algorithms={algorithms}
            count={algorithmsCount}
            index={index}
            options={options.available}
            recalculate={recalculate}
            onViewAlternative={(index) =>
              onViewAlternative(index, 'alternative')
            }
          />
        ))}
      </Flex>
      <Bleed direction="horizontal">
        <Divider orientation="left">
          {t('alternative.exceptions.label')}
        </Divider>
      </Bleed>
      <Flex vertical gap="middle">
        {exceptions.map((index) => (
          <AlternativeAlgorithmExceptionFields
            key={index}
            count={exceptionsCount}
            index={index}
            options={options.selected}
            onViewAlternative={(index) => onViewAlternative(index, 'exception')}
          />
        ))}
      </Flex>
      {viewableGroupOrAlgorithm && (
        <AlternativeAlgorithmViewModal
          algorithms={algorithms}
          viewable={viewableGroupOrAlgorithm}
          onClose={() => setViewableGroupOrAlgorithm(undefined)}
          isEditMode={true}
        />
      )}
    </Flex>
  );
}

const FormWrapper = styled(Flex)`
  overflow-y: hidden;
`;

const FullWidthFlex = styled(Flex)`
  width: 100%;
`;

const Scrollable = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
`;

const SidePanelWrapper = styled.div`
  display: grid;
  grid-template-rows: auto auto 1fr 60px;
  height: 100%;
  width: 100%;
  overflow-y: hidden;
  border-left: 1px solid ${token.colorBorderSecondary};
`;
